import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShopPurchaseInvoice } from '../../api/hooks';
import { useSession } from '../../auth/session';
import { ApiError } from '../../api/client';
import { Alert, PageHeader, Spinner } from '../../ui';
import { PurchaseInvoiceBody, PurchaseInvoiceStateChip, type PurchaseInvoiceLabels } from '../purchase-invoice/PurchaseInvoiceBody';
import { DownloadPdfButton } from '../purchase-invoice/DownloadPdfButton';
import { PayInvoiceForm } from './PayInvoiceForm';
import { PAYABLES_HOME } from './delivery-tally';

/**
 * R-27 — one of the retailer's own purchase invoices (CR-56 P1; FN-09, FN-17), reached from R-22.
 *
 * Read-only: the pieces it covers, what has been paid against it and what remains. The pieces
 * need not add up to the invoice total — tax is posted separately (FN-17, D76) — so the page shows
 * the subtotal, the adjustments and the recorded total together rather than hiding the gap.
 *
 * An invoice that is not this retailer's answers 404 from the API, so "not yours" and "does not
 * exist" read identically here as well: one message, nothing to tell them apart.
 */
export function ShopPurchaseInvoiceDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { data: inv, isLoading, error } = useShopPurchaseInvoice(id);
  const { me } = useSession();
  // `shop.pay` is the permission every retailer payment already takes (no new code, D90/D91).
  const mayPay = me?.kind === 'retailer' && me.permissions.includes('shop.pay');

  const labels: PurchaseInvoiceLabels = {
    piecesTitle: t('purchaseInvoice.piecesTitle'),
    piecesEmpty: t('purchaseInvoice.piecesEmpty'),
    colJob: t('purchaseInvoice.colJob'),
    colDescription: t('purchaseInvoice.colDescription'),
    colValue: t('purchaseInvoice.colValue'),
    subtotal: t('purchaseInvoice.subtotal'),
    adjustments: t('purchaseInvoice.adjustments'),
    total: t('purchaseInvoice.total'),
    paymentsTitle: t('purchaseInvoice.paymentsTitle'),
    paymentsEmpty: t('purchaseInvoice.paymentsEmpty'),
    colReceived: t('purchaseInvoice.colReceived'),
    colBankReference: t('purchaseInvoice.colBankReference'),
    colAmount: t('purchaseInvoice.colAmount'),
    paid: t('purchaseInvoice.paid'),
    outstanding: t('purchaseInvoice.outstanding'),
    submissionsTitle: t('purchaseInvoice.submissionsTitle'),
    submissionsNote: t('purchaseInvoice.submissionsNote'),
    colSubmitted: t('purchaseInvoice.colSubmitted'),
    colStatus: t('purchaseInvoice.colStatus'),
    colProof: t('purchaseInvoice.colProof'),
    viewProof: t('purchaseInvoice.viewProof'),
    noProof: t('purchaseInvoice.noProof'),
    submissionStatus: { PENDING: t('purchaseInvoice.submissionStatus.PENDING'), REJECTED: t('purchaseInvoice.submissionStatus.REJECTED') },
    state: {
      ISSUED: t('payables.state.ISSUED'),
      PART_PAID: t('payables.state.PART_PAID'),
      PAID: t('payables.state.PAID'),
      CANCELLED: t('payables.state.CANCELLED'),
    },
  };
  const back = <div className="mk-backlink"><Link to={PAYABLES_HOME}>&larr; {t('purchaseInvoice.back')}</Link></div>;

  if (isLoading) return <div role="status"><Spinner /></div>;
  if (error) {
    return (
      <>
        {back}
        <Alert tone="stop">
          {error instanceof ApiError && error.status === 404 ? t('purchaseInvoice.notFound') : t('purchaseInvoice.loadFailed')}
        </Alert>
      </>
    );
  }
  if (!inv) return null;

  return (
    <>
      {back}
      <PageHeader
        title={inv.reference}
        sub={t('purchaseInvoice.sub', { issued: inv.issuedOn, due: inv.dueOn })}
        actions={(
          <>
            <PurchaseInvoiceStateChip state={inv.state} labels={labels} />
            <DownloadPdfButton
              path={`/shop/payables/invoices/${inv.id}/download`}
              fallbackName={`${inv.reference.replace(/[^A-Za-z0-9_-]/g, '_')}.pdf`}
              label={t('purchaseInvoice.download')}
              failed={t('purchaseInvoice.downloadFailed')}
            />
          </>
        )}
      />
      <PurchaseInvoiceBody inv={inv} labels={labels} fileHref={(proofId) => `/api/shop/payments/${proofId}/file`} />
      {/* Only an invoice that still owes something can be paid; a settled or cancelled one cannot. */}
      {mayPay && (inv.state === 'ISSUED' || inv.state === 'PART_PAID') ? <PayInvoiceForm inv={inv} /> : null}
    </>
  );
}
